import React from 'react';
import { Box, Stack, Typography, Link } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import GoogleButton from './GoogleButton';
import LoginForm from './LoginForm';
import GoogleOAuthService from '../../services/GoogleOAuthService';
import { usePermissions } from '../../contexts/PermissionContext';
import logger from '../../utils/logger';

const RightPanelContainer = styled(Box)(({ theme }) => ({
  width: '50%',
  minHeight: '100vh',
  backgroundColor: '#F5F5F5',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(5),
  position: 'relative',
  [theme.breakpoints.down('md')]: {
    width: '100%',
    minHeight: 'auto',
    padding: theme.spacing(4, 3)
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(3, 2)
  }
}));

const SignInCard = styled(Box)(({ theme }) => ({
  backgroundColor: '#FFFFFF',
  border: '1px solid #191919',
  borderRadius: '4px',
  padding: '40px',
  width: '500px',
  minHeight: '600px',
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
  [theme.breakpoints.down('md')]: {
    width: '450px',
    minHeight: '550px'
  },
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    maxWidth: '420px',
    padding: '32px 24px',
    gap: '20px',
    minHeight: 'auto'
  }
}));

const SignInHeading = styled(Typography)(({ theme }) => ({
  fontFamily: 'PolySans, "Helvetica Neue", Arial, sans-serif',
  fontSize: '32px',
  fontWeight: 600,
  color: '#303030',
  textAlign: 'center',
  lineHeight: '40px',
  [theme.breakpoints.down('sm')]: {
    fontSize: '28px',
    lineHeight: '36px'
  }
}));

const NewAccountText = styled(Typography)(({ theme }) => ({
  fontFamily: 'PolySans, "Helvetica Neue", Arial, sans-serif',
  fontSize: '14px',
  fontWeight: 400,
  color: '#303030',
  textAlign: 'center',
  lineHeight: '20px',
  [theme.breakpoints.down('sm')]: {
    fontSize: '13px'
  }
}));

const CreateAccountLink = styled(Link)(() => ({
  color: '#191919',
  textDecoration: 'underline',
  cursor: 'pointer',
  fontWeight: 400,
  '&:hover': {
    color: '#000000',
  },
}));

const PrivacyText = styled(Typography)(({ theme }) => ({
  fontFamily: 'PolySans, "Helvetica Neue", Arial, sans-serif',
  fontSize: '12px',
  fontWeight: 400,
  color: '#707070',
  textAlign: 'center',
  lineHeight: '18px',
  marginTop: '8px',
  [theme.breakpoints.down('sm')]: {
    fontSize: '11px',
    lineHeight: '16px'
  }
}));

const PrivacyLink = styled(Link)(() => ({
  color: '#707070',
  textDecoration: 'underline',
  cursor: 'pointer',
  '&:hover': {
    color: '#303030',
  },
}));

const RightPanel = () => {
  const navigate = useNavigate();
  const { checkUserPermissions } = usePermissions();

  const handleGoogleLogin = async () => {
    try {
      await GoogleOAuthService.initiateOAuthFlow({
        forceConsent: true
      });
    } catch (error) {
      logger.error('Google Login Failed:', error?.message || error);
      alert('Failed to initiate Google login. Please try again.');
    }
  };

  return (
    <RightPanelContainer>
      <SignInCard>
        <SignInHeading>Sign in</SignInHeading>
        
        <GoogleButton onClick={handleGoogleLogin} />

        <LoginForm />

        <NewAccountText>
          New here?{' '}
          <CreateAccountLink onClick={() => navigate('/register')}>
            Create an account
          </CreateAccountLink>
        </NewAccountText>

        <PrivacyText>
          By continuing, you confirm you are 18+ and agree to the{' '}
          <PrivacyLink onClick={() => navigate('/privacy-policy')}>
            Privacy Policy
          </PrivacyLink>
          {' '}and{' '}
          <PrivacyLink onClick={() => navigate('/terms-of-service')}>
            Terms of Use
          </PrivacyLink>
          .
        </PrivacyText>
      </SignInCard>
    </RightPanelContainer>
  );
};

export default RightPanel;